import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Link2, Server, Loader2, CheckCircle2, XCircle, Plus,
  Users, Download, AlertCircle, Mail, Search, UserPlus
} from 'lucide-react';
import SigmaLoginCard from '../components/SigmaLoginCard';
import SyncCustomersCard from '../components/SyncCustomersCard';
import CompleteDataCard from '../components/CompleteDataCard';
import IntegrationsPlanCard from '../components/IntegrationsPlanCard';

export default function IntegrationsPage() {
  const { api, user } = useAuthStore();
  const [integrationsVersion, setIntegrationsVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [activeIntegrationId, setActiveIntegrationId] = useState(null);
  
  // Estado de vinculação
  const [linking, setLinking] = useState(false);
  const [linkStep, setLinkStep] = useState('form'); // form, code, success
  const [linkForm, setLinkForm] = useState({ serverCode: '', sigmaUsername: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Estado de busca/importação
  const [searchMode, setSearchMode] = useState('single'); // single ou batch
  const [singleUsername, setSingleUsername] = useState('');
  const [batchUsernames, setBatchUsernames] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedClients, setSelectedClients] = useState({});
  const [clientsData, setClientsData] = useState({});
  const [packagePrices, setPackagePrices] = useState({}); // Preços dos pacotes novos
  const [packageMonths, setPackageMonths] = useState({}); // Meses dos pacotes novos
  
  // Planos e conexões
  const [connections, setConnections] = useState([]);
  const [importForm, setImportForm] = useState({ connection_id: '' });
  const [importing, setImporting] = useState(false);
  
  // Estado para importação de pacotes
  const [packagesModalOpen, setPackagesModalOpen] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [sigmaPackages, setSigmaPackages] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState({});
  const [packageFormData, setPackageFormData] = useState({}); // { packageId: { price, duration_months } }
  const [importingPackages, setImportingPackages] = useState(false);
  
  // Estado para plano de substituição de pacotes de teste
  const [customerPlans, setCustomerPlans] = useState([]);
  const [testPackagePlanId, setTestPackagePlanId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [integrationRes, connectionsRes, plansRes] = await Promise.all([
        api.get('/sigma/integration').catch(() => ({ data: null })),
        api.get('/connections').catch(() => ({ data: [] })),
        api.get('/customer-plans').catch(() => ({ data: [] }))
      ]);
      
      const list = Array.isArray(integrationRes.data) ? integrationRes.data : (integrationRes.data ? [integrationRes.data] : []);
      setIntegrations(list);
      if (list.length > 0 && !activeIntegrationId) setActiveIntegrationId(list[0].id);
      setConnections((connectionsRes.data || []).filter(c => c.status === 'connected'));
      setCustomerPlans(plansRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async () => {
    if (!linkForm.serverCode || !linkForm.sigmaUsername) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSendingCode(true);
    try {
      const response = await api.post('/sigma/link/request-code', {
        server_code: linkForm.serverCode,
        sigma_username: linkForm.sigmaUsername
      });
      
      setMaskedEmail(response.data.email);
      setLinkStep('code');
      toast.success('Código enviado para seu email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar código');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post('/sigma/link/verify', {
        server_code: linkForm.serverCode,
        sigma_username: linkForm.sigmaUsername,
        verification_code: verificationCode
      });
      
      setIntegrations(prev => [...prev, response.data]);
      if (!activeIntegrationId) setActiveIntegrationId(response.data.id);
      setLinkStep('success');
      toast.success('Integração vinculada com sucesso!');
      
      setTimeout(() => {
        setLinking(false);
        setLinkStep('form');
        setVerificationCode('');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifying(false);
    }
  };

  const handleDeactivate = async (integrationId) => {
    if (!confirm('Tem certeza que deseja desativar esta integração?')) return;
    
    try {
      await api.delete(`/sigma/integration/${integrationId}`);
      setIntegrations(prev => prev.filter(i => i.id !== integrationId));
      if (activeIntegrationId === integrationId) {
        const remaining = integrations.filter(i => i.id !== integrationId);
        setActiveIntegrationId(remaining.length > 0 ? remaining[0].id : null);
      }
      setSearchResults(null);
      toast.success('Integração desativada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar');
    }
  };

  // ========== FUNÇÕES DE IMPORTAÇÃO DE PACOTES ==========
  const handleOpenPackagesModal = async () => {
    setPackagesModalOpen(true);
    setLoadingPackages(true);
    setSigmaPackages([]);
    setSelectedPackages({});
    setPackageFormData({});
    
    try {
      const response = await api.get('/sigma/packages');
      const packages = response.data || [];
      setSigmaPackages(packages);
      
      // Inicializar formulário com valores padrão
      const formData = {};
      const selected = {};
      packages.forEach(pkg => {
        formData[pkg.id] = { price: '', duration_months: 1 };
        selected[pkg.id] = false;
      });
      setPackageFormData(formData);
      setSelectedPackages(selected);
      
    } catch (error) {
      toast.error('Erro ao buscar pacotes do Sigma');
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleTogglePackage = (packageId) => {
    setSelectedPackages(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  const handlePackageDataChange = (packageId, field, value) => {
    setPackageFormData(prev => ({
      ...prev,
      [packageId]: {
        ...prev[packageId],
        [field]: value
      }
    }));
  };

  const handleImportPackages = async () => {
    // Filtrar apenas os selecionados
    const packagesToImport = sigmaPackages
      .filter(pkg => selectedPackages[pkg.id])
      .map(pkg => {
        const formData = packageFormData[pkg.id] || {};
        return {
          package_id: pkg.id,
          package_name: pkg.name,
          price: parseFloat(formData.price) || 0,
          duration_months: parseInt(formData.duration_months) || 1
        };
      });
    
    if (packagesToImport.length === 0) {
      toast.error('Selecione pelo menos um pacote');
      return;
    }
    
    // Validar preços
    const invalidPrices = packagesToImport.filter(p => p.price <= 0);
    if (invalidPrices.length > 0) {
      toast.error('Preencha o preço de todos os pacotes selecionados');
      return;
    }
    
    setImportingPackages(true);
    try {
      const response = await api.post('/sigma/import-packages', { packages: packagesToImport });
      toast.success(response.data.message);
      setPackagesModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar pacotes');
    } finally {
      setImportingPackages(false);
    }
  };
  // ========== FIM FUNÇÕES DE PACOTES ==========

  const handleSearch = async () => {
    const usernames = searchMode === 'single' ? singleUsername : batchUsernames;
    
    if (!usernames.trim()) {
      toast.error('Informe pelo menos um username');
      return;
    }

    setSearching(true);
    setSearchResults(null);
    
    try {
      const response = await api.post('/sigma/search-clients', { usernames });
      setSearchResults(response.data);
      
      // Inicializar seleção (todos selecionados por padrão)
      const selected = {};
      const data = {};
      const prices = {};
      response.data.found.forEach(c => {
        selected[c.sigma_username] = true;
        data[c.sigma_username] = {
          phone: c.whatsapp || '',
          name: c.name || c.sigma_username
        };
        // Inicializar preço do pacote se ainda não existir
        if (c.sigma_package_name && !prices[c.sigma_package_name]) {
          prices[c.sigma_package_name] = '';
        }
      });
      setSelectedClients(selected);
      setClientsData(data);
      setPackagePrices(prev => ({ ...prev, ...prices }));
      
      // Feedback
      if (response.data.found.length > 0) {
        toast.success(`${response.data.found.length} cliente(s) encontrado(s)!`);
      }
      if (response.data.not_found.length > 0) {
        toast.error(`${response.data.not_found.length} não encontrado(s): ${response.data.not_found.join(', ')}`);
      }
      if (response.data.already_imported.length > 0) {
        toast.warning(`${response.data.already_imported.length} já importado(s): ${response.data.already_imported.join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar clientes');
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async () => {
    if (!importForm.connection_id) {
      toast.error('Selecione a conexão');
      return;
    }

    const clientsToImport = searchResults.found
      .filter(c => selectedClients[c.sigma_username])
      .map(c => ({
        ...c,
        phone: clientsData[c.sigma_username]?.phone || '',
        name: clientsData[c.sigma_username]?.name || c.name || c.sigma_username
      }));

    if (clientsToImport.length === 0) {
      toast.error('Selecione pelo menos um cliente');
      return;
    }
    
    // Verificar se há pacotes de teste e se foi selecionado um plano de substituição
    const testKeywords = ['teste', 'test', 'trial', 'demo', 'gratis', 'grátis', 'free', 'hora', 'horas', 'dia', 'dias', '24h', '48h', '72h'];
    const hasTestPackages = clientsToImport.some(c => {
      const pkgName = (c.sigma_package_name || '').toLowerCase();
      return testKeywords.some(kw => pkgName.includes(kw));
    });
    
    if (hasTestPackages && !testPackagePlanId) {
      toast.error('Selecione um plano para substituir os pacotes de teste');
      return;
    }

    setImporting(true);
    try {
      const response = await api.post('/sigma/import-clients', {
        clients: clientsToImport,
        connection_id: importForm.connection_id,
        package_prices: packagePrices,
        package_months: packageMonths,
        test_package_plan_id: testPackagePlanId || null
      });
      
      toast.success(response.data.message);
      
      // Limpar resultados
      setSearchResults(null);
      setSingleUsername('');
      setBatchUsernames('');
      setPackagePrices({});
      setPackageMonths({});
      setTestPackagePlanId('');
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Link2 className="w-8 h-8 text-primary" />
          Integrações
        </h1>
        <p className="text-muted-foreground">Conecte sua conta a sistemas externos</p>
      </div>

      {/* Integração Sigma via Login (painel/usuário/senha) */}
      <IntegrationsPlanCard api={api} refreshKey={integrationsVersion} />

      <SigmaLoginCard api={api} connections={connections} onChanged={() => setIntegrationsVersion((v) => v + 1)} />

      {/* Sincronizar clientes já cadastrados com uma integração */}
      <SyncCustomersCard key={`sync-${integrationsVersion}`} api={api} />

      {/* Completar dados (plano) dos clientes importados sem plano */}
      <CompleteDataCard key={`complete-${integrationsVersion}`} api={api} />

      {/* Card de Integração Sigma (modo antigo, por código do servidor) — só para admin */}
      {user?.role === 'admin' && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle>Sigma Panel</CardTitle>
                <CardDescription>Integração com painel de streaming</CardDescription>
              </div>
            </div>
            {integrations.length > 0 && (
              <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {integrations.length} servidor{integrations.length > 1 ? 'es' : ''}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {integrations.length > 0 ? (
            <div className="space-y-6">
              {/* Lista de integrações */}
              <div className="space-y-2">
                {integrations.map(integ => (
                  <div key={integ.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${activeIntegrationId === integ.id ? 'border-primary bg-primary/5' : 'border-border/50 bg-muted/20 hover:bg-muted/40'}`} onClick={() => setActiveIntegrationId(integ.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/15 flex items-center justify-center shrink-0">
                        <Server className="w-4 h-4 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{integ.server_name}</p>
                        <p className="text-xs text-muted-foreground">{integ.sigma_username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeIntegrationId === integ.id && <Badge variant="outline" className="text-[10px] text-primary border-primary/30">Selecionado</Badge>}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); handleDeactivate(integ.id); }}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Botão adicionar outra integração */}
              <Button variant="outline" onClick={() => { setLinking(true); setLinkStep('form'); }} className="w-full border-dashed">
                <Plus className="w-4 h-4 mr-2" />Vincular Outro Servidor
              </Button>

              {/* Importar Pacotes */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Importar Pacotes do Sigma</h3>
                  </div>
                  <Button onClick={handleOpenPackagesModal} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Importar Pacotes
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Importe os pacotes do Sigma como planos. Defina o preço e duração de cada um.
                </p>
              </div>

              {/* Busca de Clientes */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Importar Clientes do Sigma</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Ao importar clientes, se o pacote já foi importado como plano, ele será usado automaticamente.
                </p>

                {/* Tabs de modo */}
                <div className="flex gap-2">
                  <Button 
                    variant={searchMode === 'single' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSearchMode('single')}
                  >
                    Um Cliente
                  </Button>
                  <Button 
                    variant={searchMode === 'batch' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSearchMode('batch')}
                  >
                    Vários (Lista)
                  </Button>
                </div>

                {/* Campo de busca */}
                {searchMode === 'single' ? (
                  <div className="flex gap-2">
                    <Input
                      value={singleUsername}
                      onChange={(e) => setSingleUsername(e.target.value)}
                      placeholder="Username do cliente no Sigma"
                      className="flex-1"
                      data-testid="sigma-client-username"
                    />
                    <Button onClick={handleSearch} disabled={searching}>
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Textarea
                      value={batchUsernames}
                      onChange={(e) => setBatchUsernames(e.target.value)}
                      placeholder="Cole os usernames (um por linha ou separados por vírgula)"
                      rows={4}
                      data-testid="sigma-batch-usernames"
                    />
                    <Button onClick={handleSearch} disabled={searching} className="w-full">
                      {searching ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Buscar Clientes
                    </Button>
                  </div>
                )}

                {/* Resultados da busca */}
                {searchResults && searchResults.found.length > 0 && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Clientes Encontrados ({searchResults.found.length})</h4>
                    </div>

                    {/* Config de importação */}
                    <div className="p-3 bg-muted/50 rounded-lg space-y-3">
                      <div>
                        <Label className="text-xs">Conexão WhatsApp</Label>
                        <Select
                          value={importForm.connection_id}
                          onValueChange={(v) => setImportForm({ ...importForm, connection_id: v })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecione a conexão" />
                          </SelectTrigger>
                          <SelectContent>
                            {connections.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Preços dos pacotes */}
                      {Object.keys(packagePrices).length > 0 && (
                        <div className="border-t pt-3">
                          <Label className="text-xs mb-2 block">Configurar Planos (novos serão criados automaticamente)</Label>
                          <div className="grid gap-2">
                            {Object.keys(packagePrices).map(pkgName => (
                              <div key={pkgName} className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground flex-1 truncate" title={pkgName}>
                                  {pkgName}
                                </span>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-muted-foreground">R$</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={packagePrices[pkgName]}
                                    onChange={(e) => setPackagePrices(prev => ({
                                      ...prev,
                                      [pkgName]: e.target.value
                                    }))}
                                    placeholder="0.00"
                                    className="w-20 h-8 text-sm"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="12"
                                    value={packageMonths[pkgName] || '1'}
                                    onChange={(e) => setPackageMonths(prev => ({
                                      ...prev,
                                      [pkgName]: e.target.value
                                    }))}
                                    placeholder="1"
                                    className="w-14 h-8 text-sm"
                                  />
                                  <span className="text-xs text-muted-foreground">mês</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tabela de clientes */}
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox
                                checked={searchResults.found.every(c => selectedClients[c.sigma_username])}
                                onCheckedChange={(checked) => {
                                  const newSelected = {};
                                  searchResults.found.forEach(c => {
                                    newSelected[c.sigma_username] = checked;
                                  });
                                  setSelectedClients(newSelected);
                                }}
                              />
                            </TableHead>
                            <TableHead>Username Sigma</TableHead>
                            <TableHead>Pacote</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead>WhatsApp</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {searchResults.found.map((client) => (
                            <TableRow key={client.sigma_username}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedClients[client.sigma_username] || false}
                                  onCheckedChange={(checked) => {
                                    setSelectedClients(prev => ({
                                      ...prev,
                                      [client.sigma_username]: checked
                                    }));
                                  }}
                                />
                              </TableCell>
                              <TableCell className="font-medium font-mono text-sm">
                                {client.sigma_username}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {client.sigma_package_name}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {client.expiration_date}
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={clientsData[client.sigma_username]?.phone || ''}
                                  onChange={(e) => setClientsData(prev => ({
                                    ...prev,
                                    [client.sigma_username]: {
                                      ...prev[client.sigma_username],
                                      phone: e.target.value
                                    }
                                  }))}
                                  placeholder="5521999999999"
                                  className="w-36 h-8 text-sm"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Seleção de plano para pacotes de teste */}
                    {searchResults.found.some(c => {
                      const testKeywords = ['teste', 'test', 'trial', 'demo', 'gratis', 'grátis', 'free', 'hora', 'horas', 'dia', 'dias', '24h', '48h', '72h'];
                      const pkgName = (c.sigma_package_name || '').toLowerCase();
                      return testKeywords.some(kw => pkgName.includes(kw));
                    }) && (
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                          <p className="text-sm font-medium text-amber-500">
                            Pacotes de teste detectados
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Alguns clientes possuem pacotes de teste. Selecione um plano existente para substituí-los:
                        </p>
                        <Select value={testPackagePlanId} onValueChange={setTestPackagePlanId}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione um plano para pacotes de teste..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customerPlans.map((plan) => (
                              <SelectItem key={plan.id} value={plan.id}>
                                {plan.name} - R$ {plan.price?.toFixed(2)} ({plan.duration_months} {plan.duration_months === 1 ? 'mês' : 'meses'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {customerPlans.length === 0 && (
                          <p className="text-xs text-red-500">
                            Nenhum plano encontrado. Crie um plano primeiro na página de Planos.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Botão importar */}
                    <Button 
                      onClick={handleImport} 
                      disabled={importing || !importForm.connection_id}
                      className="w-full"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <UserPlus className="w-4 h-4 mr-2" />
                      )}
                      Importar para o Gestor ({Object.values(selectedClients).filter(Boolean).length})
                    </Button>
                  </div>
                )}

                {/* Mensagens de não encontrados */}
                {searchResults && searchResults.not_found.length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-500">Não encontrados:</p>
                      <p className="text-xs text-red-400">{searchResults.not_found.join(', ')}</p>
                    </div>
                  </div>
                )}

                {searchResults && searchResults.already_imported.length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-500">Já importados por você:</p>
                      <p className="text-xs text-yellow-400">{searchResults.already_imported.join(', ')}</p>
                    </div>
                  </div>
                )}

                {searchResults && searchResults.imported_by_others?.length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-orange-500">Já cadastrados por outro usuário:</p>
                      <p className="text-xs text-orange-400">{searchResults.imported_by_others.join(', ')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* (botões individuais na lista acima) */}
            </div>
          ) : linking ? (
            <div className="space-y-4">
              {linkStep === 'form' && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Código do Servidor</Label>
                      <Input
                        value={linkForm.serverCode}
                        onChange={(e) => setLinkForm({ ...linkForm, serverCode: e.target.value.toUpperCase() })}
                        placeholder="Ex: SIGMA01"
                        data-testid="sigma-server-code-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Digite o código fornecido pelo administrador
                      </p>
                    </div>
                    <div>
                      <Label>Seu Usuário no Sigma</Label>
                      <Input
                        value={linkForm.sigmaUsername}
                        onChange={(e) => setLinkForm({ ...linkForm, sigmaUsername: e.target.value })}
                        placeholder="usuario_sigma"
                        data-testid="sigma-username-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleRequestCode} disabled={sendingCode}>
                      {sendingCode ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Receber Código
                    </Button>
                    <Button variant="ghost" onClick={() => setLinking(false)}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}

              {linkStep === 'code' && (
                <>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Mail className="w-10 h-10 mx-auto mb-2 text-primary" />
                    <p className="font-medium">Código enviado!</p>
                    <p className="text-sm text-muted-foreground">
                      Verifique seu email: {maskedEmail}
                    </p>
                  </div>
                  <div>
                    <Label>Código de Verificação</Label>
                    <Input
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-2xl font-mono tracking-[0.5em]"
                      maxLength={6}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyCode} disabled={verifying || verificationCode.length !== 6}>
                      {verifying ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => { setLinkStep('form'); setVerificationCode(''); }}>
                      Voltar
                    </Button>
                  </div>
                </>
              )}

              {linkStep === 'success' && (
                <div className="text-center p-8">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <p className="text-xl font-medium text-green-500">Vinculado com sucesso!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Server className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">Nenhuma integração ativa</p>
              <Button onClick={() => setLinking(true)}>
                <Link2 className="w-4 h-4 mr-2" />
                Vincular Conta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Modal de Importação de Pacotes */}
      <Dialog open={packagesModalOpen} onOpenChange={setPackagesModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Importar Pacotes do Sigma</DialogTitle>
            <DialogDescription>
              Selecione os pacotes que deseja importar como planos. Defina o preço e duração de cada um.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {loadingPackages ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : sigmaPackages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum pacote encontrado no Sigma</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={Object.values(selectedPackages).every(v => v) && sigmaPackages.length > 0}
                        onCheckedChange={(checked) => {
                          const newSelected = {};
                          sigmaPackages.forEach(pkg => {
                            newSelected[pkg.id] = checked;
                          });
                          setSelectedPackages(newSelected);
                        }}
                      />
                    </TableHead>
                    <TableHead>Pacote</TableHead>
                    <TableHead className="w-32">Preço (R$)</TableHead>
                    <TableHead className="w-32">Duração (meses)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sigmaPackages.map((pkg) => (
                    <TableRow key={pkg.id} className={selectedPackages[pkg.id] ? 'bg-primary/5' : ''}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedPackages[pkg.id] || false}
                          onCheckedChange={() => handleTogglePackage(pkg.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{pkg.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {pkg.id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={packageFormData[pkg.id]?.price || ''}
                          onChange={(e) => handlePackageDataChange(pkg.id, 'price', e.target.value)}
                          disabled={!selectedPackages[pkg.id]}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="1"
                          placeholder="1"
                          value={packageFormData[pkg.id]?.duration_months || 1}
                          onChange={(e) => handlePackageDataChange(pkg.id, 'duration_months', e.target.value)}
                          disabled={!selectedPackages[pkg.id]}
                          className="w-20"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-muted-foreground">
                {Object.values(selectedPackages).filter(v => v).length} pacote(s) selecionado(s)
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPackagesModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleImportPackages} 
                  disabled={importingPackages || Object.values(selectedPackages).filter(v => v).length === 0}
                >
                  {importingPackages ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Importar Selecionados
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
